import { Observable, Subscription } from 'rxjs';
import type { ApiResponse } from '../types';
import type { FamilyFormOption } from '../types/family-member-form.types';
import type { FamilyPlanMemberView, FamilyPlanView } from '../types/family-plan-view.types';
import type { RemoveMemberPayload } from '../types/family-members.types';
import {
  familyRemoveErrorMessage,
  needsNewPrimaryPick,
  newPrimaryCandidates,
  newPrimaryOptions,
} from './family-remove.util';

/** The call this controller makes; implemented by FamilyMembersService. */
export interface FamilyRemoveActions {
  removeMember(
    groupId: string,
    memberId: string,
    payload: RemoveMemberPayload
  ): Observable<ApiResponse<unknown>>;
}

export interface FamilyRemoveOptions {
  /** The plate was taken out: reload the family. */
  onRemoved?: (member: FamilyPlanMemberView) => void;
}

/**
 * State behind the "Remove member" pop-up on the Family Plan tab: which plate, who takes over when
 * the primary leaves, and the one request. The cases that move a card are refused by the server;
 * they are named in the message rather than half done here.
 */
export class FamilyRemoveFlowController {
  member: FamilyPlanMemberView | null = null;
  candidates: FamilyFormOption[] = [];
  needsPick = false;
  newPrimaryId = '';
  busy = false;
  error = '';

  private groupId = '';
  private request = Subscription.EMPTY;

  constructor(
    private readonly actions: FamilyRemoveActions,
    private readonly options: FamilyRemoveOptions = {}
  ) {}

  get isOpen(): boolean {
    return this.member !== null;
  }

  /** The confirm button waits for a pick when the primary leaves a family of three or more. */
  get canConfirm(): boolean {
    return !this.busy && (!this.needsPick || this.newPrimaryId !== '');
  }

  open(view: FamilyPlanView | null, member: FamilyPlanMemberView): void {
    this.close();
    this.groupId = view?.group?.id ?? '';
    this.member = member;
    this.needsPick = needsNewPrimaryPick(view, member);
    this.candidates = newPrimaryOptions(newPrimaryCandidates(view, member));
    this.newPrimaryId = '';
  }

  close(): void {
    this.request.unsubscribe();
    this.member = null;
    this.candidates = [];
    this.needsPick = false;
    this.newPrimaryId = '';
    this.busy = false;
    this.error = '';
  }

  confirm(): void {
    const member = this.member;
    if (!member || !this.canConfirm || !this.groupId) {
      return;
    }
    this.busy = true;
    this.error = '';
    this.request = this.actions
      .removeMember(this.groupId, member.member_id, {
        ...(this.needsPick ? { new_primary_member_id: this.newPrimaryId } : {}),
      })
      .subscribe({
        next: () => {
          this.close();
          this.options.onRemoved?.(member);
        },
        error: error => {
          this.busy = false;
          this.error = familyRemoveErrorMessage(error);
        },
      });
  }

  destroy(): void {
    this.request.unsubscribe();
  }
}
